import type {
  ColumnFiltersState,
  ColumnSizingState,
  PaginationState,
  SortingState,
  VisibilityState,
} from "@tanstack/vue-table";

import { defineStore } from "pinia";
import { ref } from "vue";

/**
 * Per-table persisted state. Add future controlled slices here (columnOrder,
 * ...) along with a default in each consumer's defaults factory — getOrCreate's
 * merge upgrades entries created before the change.
 */
export interface DataTableState {
  columnFilters: ColumnFiltersState;
  columnSizing: ColumnSizingState;
  columnVisibility: VisibilityState;
  globalFilter: string;
  pagination: PaginationState;
  sorting: SortingState;
}

/**
 * Session-only persistence for DataTable state, keyed by the consumer's
 * `stateKey`. In-memory by design: survives route navigation
 * (unmount/remount), cleared on page reload. See
 * docs/superpowers/specs/2026-06-12-datatable-state-persistence-design.md.
 */
export const useTableStateStore = defineStore("tableState", () => {
  const tables = ref<Record<string, DataTableState>>({});
  /**
   * Where a cases table's viewer pressed Stop loading: by stateKey, then by the slice (firm, or
   * "all") it was pressed on. That slice doesn't start loading every case again when the viewer
   * comes back to it, from another firm or a later visit. "Load all cases" there clears it.
   */
  const caseLoadStopped = ref<Record<string, Record<string, true>>>({});

  /**
   * Return the persisted entry for `key`, seeding it from `defaults` on
   * first use. For an already-existing entry the object identity is
   * preserved — only slices that are absent from the entry are copied in
   * from `defaults` (defensive merge for slices added to DataTableState
   * after the entry was created). The user's surviving values always win.
   * Always returns the reactive store entry, never the raw `defaults` object.
   */
  const getOrCreate = (key: string, defaults: DataTableState): DataTableState => {
    const existing = tables.value[key];
    if (!existing) {
      tables.value[key] = defaults;
      return tables.value[key];
    }
    for (const [slice, value] of Object.entries(defaults)) {
      if (!(slice in existing)) {
        (existing as unknown as Record<string, unknown>)[slice] = value;
      }
    }
    return existing;
  };

  /**
   * Set, replace, or remove a single column filter on an existing entry — the
   * channel external writers (e.g. chart clicks) use to drive a mounted
   * DataTable. `value === undefined` removes the filter. No-ops when the entry
   * doesn't exist yet (the table seeds it on mount; chart clicks only happen
   * after that), so we never create a partial DataTableState here.
   *
   * Resetting pageIndex is load-bearing: this path bypasses TanStack's
   * `setColumnFilters`, so its `autoResetPageIndex` (which the in-table filter
   * UI relies on) never fires. Without this, a user on a deep page who applies
   * a chart filter that shrinks the result set stays on a now-empty page.
   */
  const setColumnFilter = (key: string, columnId: string, value: unknown): void => {
    const entry = tables.value[key];
    if (!entry) return;
    const others = entry.columnFilters.filter((filter) => filter.id !== columnId);
    entry.columnFilters = value === undefined ? others : [...others, { id: columnId, value }];
    entry.pagination.pageIndex = 0;
  };

  /** Every table's state, dropped on sign-out (`clearAllStores`): the search can hold a name. */
  function $reset(): void {
    tables.value = {};
    caseLoadStopped.value = {};
  }

  /** Drop the entry for `key`; the next getOrCreate reseeds from defaults. */
  const reset = (key: string): void => {
    delete tables.value[key];
    delete caseLoadStopped.value[key];
  };

  return { $reset, caseLoadStopped, getOrCreate, reset, setColumnFilter, tables };
});
